import { type DragEvent, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import { GripIcon, XIcon } from '@ttab/elephant-ui/icons'
import type { MemoItem, MemoLine } from '@/shared/templates/memos/lib/renderMemo'
import { PreviewLine } from './PreviewLine'

/**
 * The value the keyword select carries for "no keyword". A select item needs a
 * value of its own, so the absence of a keyword gets a sentinel rather than the
 * empty string the item itself stores.
 */
const NO_KEYWORD = 'none'

/**
 * One selected item as the preview shows it: the lines it renders into, and
 * the controls that reach the memo from where the memo is - the handle that
 * drags it around, the cross that takes it out, and the keyword its block
 * asks for.
 */
export const PreviewItem = ({
  item,
  lines,
  keywords,
  separated,
  fixed,
  onlyItem,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onRemove,
  onKeywordChange
}: {
  item: MemoItem
  lines: MemoLine[]
  keywords: string[]
  /** Whether a hairline separates this item from the one above it. */
  separated: boolean
  /** Whether the item's time decides its place, which leaves its handle inert. */
  fixed: boolean
  /**
   * Whether this is a one-post memo's post, shown in full since the memo has
   * room for it, and with no handle since there is nothing to move it among.
   */
  onlyItem: boolean
  onDragStart: () => void
  onDragEnd: () => void
  onDragOver: (event: DragEvent<HTMLDivElement>) => void
  onDrop: (event: DragEvent<HTMLDivElement>) => void
  onRemove: () => void
  onKeywordChange: (keyword: string | undefined) => void
}): JSX.Element => {
  const { t } = useTranslation()

  return (
    // The controls and the headline are centered against one another, so that a
    // keyword select - taller than the line of text beside it - does not leave
    // the headline riding along its top edge. An only item shown in full keeps
    // them beside its first line instead.
    <div
      className={cn(
        'flex gap-2 py-1',
        onlyItem ? 'items-start' : 'items-center',
        separated && 'border-t border-border'
      )}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {!onlyItem && (
        <div
          className={cn(
            'flex shrink-0 items-center self-stretch touch-none',
            fixed
              ? 'cursor-default text-muted-foreground/40 dark:text-muted-foreground/30'
              : 'cursor-grab text-muted-foreground'
          )}
          draggable={!fixed}
          aria-disabled={fixed}
          aria-label={t('memo:actions.reorderItem', { headline: item.headline })}
          onDragStart={(event) => {
            event.dataTransfer.setData('text/plain', item.id)
            event.dataTransfer.effectAllowed = 'move'
            onDragStart()
          }}
          onDragEnd={onDragEnd}
        >
          <GripIcon size={16} strokeWidth={1.75} />
        </div>
      )}

      {/*
        Taking this copy of an item out of the memo from where the memo is. The
        column checkbox is the blunter way: it takes the whole document out -
        every copy of it, where a template put one in each block - and only for
        an item the current filter still shows.
      */}
      <Button
        variant='ghost'
        size='sm'
        className='size-6 shrink-0 p-0 text-muted-foreground'
        aria-label={t('memo:actions.removeItem', { headline: item.headline })}
        onClick={onRemove}
      >
        <XIcon size={14} strokeWidth={1.75} />
      </Button>

      {/*
        The keyword is a print layout instruction prefixed to the headline, so
        its picker sits where it will read in the finished memo. Only blocks
        that declare keywords get one.
      */}
      {keywords.length > 0 && (
        <Select
          value={item.keyword ?? NO_KEYWORD}
          onValueChange={(value) => {
            onKeywordChange(value === NO_KEYWORD ? undefined : value)
          }}
        >
          <SelectTrigger
            aria-label={t('memo:labels.keywordFor', { headline: item.headline })}
            className='h-6 w-32 shrink-0 px-2 py-0 text-xs'
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_KEYWORD}>
              {t('memo:labels.noKeyword')}
            </SelectItem>
            {keywords.map((keyword) => (
              <SelectItem key={keyword} value={keyword}>
                {keyword}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/*
        Only the headline - always the first rendered line - shows in the
        preview row. Everything else the item carries (slugline, description,
        assignments, ...) is still there, just tucked into the tooltip instead
        of taking up space in this small a window - except for an only item.
      */}
      <div className='min-w-0 grow'>
        {onlyItem
          ? lines.map((line, index) => <PreviewLine key={index} line={line} />)
          : (
              <p className='truncate' title={lines.map((line) => line.text).join('\n')}>
                {lines[0]?.text}
              </p>
            )}
      </div>
    </div>
  )
}
